import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import useWidgetStore from '../store/useWidgetStore';
import { useSettings } from './SettingsContext';
import useDataStore from '../store/useDataStore';
import { isConsumerRoute } from '../router/consumerRoutes';
const initialState = {
    insideFlow: false,
    currentStep: 0,
    steps: 0,
    views: [],
    busy: false,
    setBusy: (busy) => { },
    onNext: () => { },
    setBackCallback: (fn) => {
        console.log(fn);
    },
    direction: "next"
};
export const FlowContext = createContext(initialState);
export const useFlow = () => {
    return useContext(FlowContext);
};
export function initFlow(flow, orientation) {
    let flowState = {
        currentStep: 0,
        steps: flow.views.length,
        insideFlow: false,
        flow: flow,
        views: flow.views
    };
    if (flow.views.length > 0) {
        flowState.nextRoute = flow.views[1].route;
        let currentRoute = location.hash.substring(12), _subRoute = false;
        // I don't know exactly why, but when the company has no profiles saved yet ( and the widget id is _default instead of some random string )
        // The widget always redirects to select1 after the first load of _preview
        // select1 never exists in the horizontal widget
        if (orientation === "horizontal" && currentRoute === "select1") {
            currentRoute = "shop";
        }
        // a consumer route is outside the flow even where the config lists it as a subroute
        let currentStep = isConsumerRoute(currentRoute) ? -1 : flow.views.findIndex((view) => {
            var _a;
            let match = view.route.route === currentRoute;
            if (!match) {
                let subRoute = (_a = view.subroutes) === null || _a === void 0 ? void 0 : _a.includes(currentRoute);
                if (subRoute) {
                    _subRoute = subRoute;
                }
                return subRoute;
            }
            return match;
        });
        if (currentStep === -1) {
            // step not found
            flowState.insideFlow = false;
            flowState.currentView = undefined;
        }
        else {
            flowState.insideFlow = true;
            flowState.currentStep = currentStep;
            flowState.currentRoute = flow.views[currentStep].route;
            flowState.currentView = flow.views[currentStep];
        }
        if (currentStep < (flowState.steps - 1)) {
            flowState.nextRoute = flow.views[currentStep + 1].route;
        }
        else {
            flowState.nextRoute = undefined;
        }
        if (currentStep > 0) {
            flowState.previousRoute = flow.views[currentStep - 1].route;
        }
        else {
            flowState.previousRoute = undefined;
        }
    }
    return flowState;
}
// Resolve a neighbouring step by route IDENTITY instead of by arithmetic on a step index.
//
// determineSteps rebuilds the valid view list on every transition and it can change length:
// registering drops signIn AND register at once, so an index captured before a transition points
// at a different view afterwards. The old code compensated with "current step - 1", which only
// holds when exactly one view is swapped, and silently overshot otherwise — that is how pressing
// next after registering landed on thankYou.
//
// Walks the configured order (flow.flow.views) outwards from the current view and returns the
// first neighbour in `direction` that survived determineSteps, as an index into flow.views.
// Returns -1 when there is none. Indifferent to how many views appear or disappear.
const findAdjacentStep = (flow, direction, from) => {
    var _a, _b;
    const configViews = (_b = (_a = flow.flow) === null || _a === void 0 ? void 0 : _a.views) !== null && _b !== void 0 ? _b : flow.views;
    const fromRoute = from !== null && from !== void 0 ? from : flow.currentRoute;
    // On a subroute, handleInsideFlow swaps the subroute name into route.route and keeps the
    // original in parentRoute — mutating the view object itself, which is shared with
    // configViews. Match either spelling so a subroute still resolves to its step.
    const routeName = fromRoute === null || fromRoute === void 0 ? void 0 : fromRoute.route;
    const parentName = fromRoute === null || fromRoute === void 0 ? void 0 : fromRoute.parentRoute;
    const isCurrentView = (view) => {
        var _a;
        return view.route.route === routeName
            || (!!parentName && view.route.route === parentName)
            || !!((_a = view.subroutes) === null || _a === void 0 ? void 0 : _a.includes(routeName));
    };
    const validStepOf = (view) => flow.views.findIndex((valid) => valid.route.route === view.route.route);
    const configStep = configViews.findIndex(isCurrentView);
    if (configStep === -1) {
        // not part of the configured flow (a detour view): step relative to the valid list
        const validStep = flow.views.findIndex(isCurrentView);
        const neighbour = validStep + direction;
        return validStep !== -1 && neighbour >= 0 && neighbour < flow.views.length ? neighbour : -1;
    }
    for (let i = configStep + direction; i >= 0 && i < configViews.length; i += direction) {
        const validStep = validStepOf(configViews[i]);
        if (validStep !== -1) {
            return validStep;
        }
    }
    return -1;
};
const FlowProvider = ({ children, initial }) => {
    var _a, _b, _c, _d;
    const [flow, setFlow] = useState(Object.assign(Object.assign({}, initialState), initial));
    // the current view is waiting on a query the flow cannot move on without
    const [busy, setBusy] = useState(false);
    // the app bar title for a view outside the flow, which has no step to take a label from
    const [title, setTitle] = useState(undefined);
    // the flow step a consumer route was opened from. A ref rather than state: nothing renders
    // it, and it has to survive the re-render the navigation away from that step causes.
    const flowReturn = useRef(undefined);
    const navigate = useNavigate();
    const { isAccount, isGuest, isLoginAccount, isDirtyAccount, isNewAccount, booking, selectedApptype, selectedResource, cart, setCart, setBooking } = useWidgetStore();
    const { products } = useDataStore();
    const { hasAccounts, hasLogin, hasShop } = useSettings();
    const checkRequirement = (requirement) => {
        var _a, _b, _c, _d;
        let isValid = true;
        switch (requirement) {
            case 'guest':
                if (!isGuest)
                    isValid = false;
                break;
            case 'account':
                if (!isAccount)
                    isValid = false;
                break;
            case 'loginAccount':
                if (!isLoginAccount)
                    isValid = false;
                break;
            case 'dirtyAccount':
                if (!isDirtyAccount)
                    isValid = false;
                break;
            case 'newAccount':
                if (!isNewAccount)
                    isValid = false;
                break;
            case 'hasAccounts':
                if (!hasAccounts)
                    isValid = false;
                break;
            case 'hasLogin':
                if (!hasLogin)
                    isValid = false;
                break;
            case 'hasShop':
                if (!hasShop)
                    isValid = false;
                break;
            case 'payable':
                // if (!cart || !cart.transactions[0]?.id || cart.transactions[0]?.status !== 'pending') isValid = false
                if (!cart || !cart.order_id)
                    isValid = false;
                break;
            case 'return':
                // if (!cart || !cart.order_id) isValid = false
                break;
            case 'mandatable':
                if (!cart || !((_a = cart.mandatables[0]) === null || _a === void 0 ? void 0 : _a.mandatable))
                    isValid = false;
                break;
            case 'booking':
                if (!booking || !(booking === null || booking === void 0 ? void 0 : booking.appointments) || ((_b = booking === null || booking === void 0 ? void 0 : booking.appointments) === null || _b === void 0 ? void 0 : _b.length) === 0) {
                    isValid = false;
                    // let _booking = localStorage.getItem('storedBooking')
                    // if (_booking) {
                    //     let storedBooking = JSON.parse(_booking)
                    //     console.log(storedBooking)
                    // }
                }
                break;
            case 'selectedApptype':
                // Read live rather than through the closure's own `selectedApptype`: the view
                // that just set it (Index, silently picking a predetermined apptype) can be
                // fed a frozen FlowContext by SlideRoutes - see the comment on ViewContext -
                // so its own checkRequirement/determineSteps calls may be running on a
                // snapshot taken before the selection was made. The store itself is never
                // stale; getState() sidesteps the snapshot entirely.
                if (((_c = useWidgetStore.getState().selectedApptype) !== null && _c !== void 0 ? _c : []).length === 0)
                    isValid = false;
                break;
            case 'selectedResource':
                if (((_d = useWidgetStore.getState().selectedResource) !== null && _d !== void 0 ? _d : []).length === 0)
                    isValid = false;
                break;
            case 'optionalProducts':
                // isValid = !!products.find((product) => product.requiredApp !== true) // false or undefined
                isValid = hasShop;
                break;
            case 'requiredProducts':
                // isValid = !!products.find((product) => product.requiredApp === true) 
                isValid = false;
                break;
            case 'showCategories':
            case 'showDetails':
                break;
            default:
                // console.log('unknown requirement', requirement)
                break;
        }
        if (!isValid) {
            // console.log('requirement not met', requirement)
        }
        return isValid;
    };
    const determineSteps = (flow) => {
        var _a;
        let validFlow = flow;
        if (validFlow) {
            validFlow.views = [];
            (_a = flow === null || flow === void 0 ? void 0 : flow.flow) === null || _a === void 0 ? void 0 : _a.views.forEach((view) => {
                var _a;
                if (view.route.route === 'accountDetails') {
                    if (!view.requirements)
                        view.requirements = [];
                    if (!view.requirements.includes('dirtyAccount')) {
                        view.requirements.push('dirtyAccount');
                    }
                }
                let isValid = true, restart = false;
                if (view.requirements) {
                    // requirements that enable a view or not
                    view.requirements.forEach((requirement) => {
                        if (!checkRequirement(requirement))
                            isValid = false;
                        // if (view.route.route === 'passwordReminder1') {
                        //     if (!isValid) {
                        //         console.log(booking)
                        //         console.log(view.requirements)
                        //         console.log(!booking || !booking?.appointments || booking?.appointments?.length === 0)
                        //         console.log(`${requirement} not met for ${view.route.route}`)
                        //     }
                        // }
                    });
                    if (((_a = flow.currentView) === null || _a === void 0 ? void 0 : _a.route.route) === view.route.route) {
                        // requirements that restart the widget if not true on the current view
                        view.requirements.forEach((requirement) => {
                            if ([
                                "booking",
                                //   "selectedApptype",
                                //    "selectedResource"
                            ].includes(requirement)) {
                                if (!checkRequirement(requirement)) {
                                    console.log(`${requirement} causes restart`);
                                    isValid = false;
                                    restart = true;
                                }
                            }
                        });
                    }
                }
                if (isValid) {
                    validFlow === null || validFlow === void 0 ? void 0 : validFlow.views.push(view);
                }
                if (restart) {
                    console.log('restart');
                    navigate(`/oa_widget_${flow.views[0].route.route}`);
                }
            });
            validFlow.steps = validFlow.views.length;
        }
        return validFlow;
    };
    const handleInsideFlow = (direction) => {
        var _a;
        if (flow) {
            let validFlow = determineSteps(flow);
            if (validFlow !== null) {
                let currentRoute = location.hash.substring(12), _subRoute = false;
                // handle parameterized URLs
                if (currentRoute.includes('/')) {
                    currentRoute = currentRoute.split('/')[0];
                }
                // see initFlow: the consumer's own screens never take a step in the flow
                let currentStep = isConsumerRoute(currentRoute) ? -1 : validFlow.views.findIndex((view) => {
                    var _a;
                    let match = view.route.route === currentRoute;
                    if (!match) {
                        let subRoute = (_a = view.subroutes) === null || _a === void 0 ? void 0 : _a.includes(currentRoute);
                        if (subRoute) {
                            _subRoute = subRoute;
                        }
                        return subRoute;
                    }
                    return match;
                });
                if (currentStep === -1) {
                    // step not found
                    setFlow((prev) => (Object.assign(Object.assign({}, prev), { insideFlow: false, currentStep: validFlow.currentStep, steps: validFlow.steps, currentRoute: validFlow.nextRoute, fromRoute: validFlow.currentRoute, previousRoute: validFlow.currentRoute, nextRoute: validFlow.nextRoute, views: validFlow.views, currentView: validFlow.currentView, onNext: validFlow.onNext, setBackCallback: validFlow.setBackCallback, backCallback: flow.backCallback, direction: direction !== null && direction !== void 0 ? direction : flow.direction })));
                }
                else {
                    let _currentRoute = validFlow === null || validFlow === void 0 ? void 0 : validFlow.views[currentStep].route;
                    if (_subRoute) {
                        _currentRoute.parentRoute = _currentRoute.route;
                        _currentRoute.route = currentRoute;
                    }
                    setFlow((prev) => (Object.assign(Object.assign({}, prev), { insideFlow: true, currentStep: currentStep, steps: validFlow.steps, currentRoute: _currentRoute, previousRoute: currentStep > 0 ? validFlow === null || validFlow === void 0 ? void 0 : validFlow.views[currentStep - 1].route : undefined, nextRoute: currentStep < (validFlow.steps - 1) ? validFlow === null || validFlow === void 0 ? void 0 : validFlow.views[currentStep + 1].route : undefined, fromRoute: prev === null || prev === void 0 ? void 0 : prev.currentRoute, views: validFlow.views, currentView: validFlow.views[currentStep], onNext: validFlow.onNext, setBackCallback: validFlow.setBackCallback, backCallback: flow.backCallback, direction: direction !== null && direction !== void 0 ? direction : flow.direction })));
                }
            }
        }
        return (_a = flow === null || flow === void 0 ? void 0 : flow.insideFlow) !== null && _a !== void 0 ? _a : false;
    };
    useEffect(() => {
        var _a, _b, _c;
        if (flow === null || flow === void 0 ? void 0 : flow.insideFlow) {
            let currentRoute = location.hash.substring(12);
            if (currentRoute.includes('/')) {
                // parameterized URL, do nothing if we're  already there
                let baseRoute = currentRoute.split('/')[0];
                if (baseRoute !== ((_a = flow === null || flow === void 0 ? void 0 : flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route)) {
                    navigate(`oa_widget_${(_b = flow === null || flow === void 0 ? void 0 : flow.currentRoute) === null || _b === void 0 ? void 0 : _b.route}`);
                }
            }
            else {
                navigate(`oa_widget_${(_c = flow === null || flow === void 0 ? void 0 : flow.currentRoute) === null || _c === void 0 ? void 0 : _c.route}`);
            }
        }
    }, [flow === null || flow === void 0 ? void 0 : flow.currentRoute]);
    const handleNext = () => {
        var _a, _b, _c, _d, _e, _f;
        if (flow) {
            handleInsideFlow("next");
            // handleInsideFlow above re-ran determineSteps, which rewrites flow.views in place,
            // so flow.views is the currently valid subset and flow.flow.views the configured order.
            let _nextStep = findAdjacentStep(flow, 1);
            // A consumer who has just registered already filled in everything accountDetails
            // asks for — it is the same RegisterForm in another mode, minus the password and
            // consent sections — so step over it on the way forward. The view stays in the flow
            // rather than being dropped, so pressing back from the summary still lands on it.
            // isNewAccount is what distinguishes this from signing in, where the stored details
            // have not been reviewed this session and accountDetails should be shown.
            const _leavingRoute = (_b = (_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.parentRoute) !== null && _b !== void 0 ? _b : (_c = flow.currentRoute) === null || _c === void 0 ? void 0 : _c.route;
            if (_nextStep !== -1
                && isNewAccount
                && _leavingRoute === 'register'
                && ((_d = flow.views[_nextStep]) === null || _d === void 0 ? void 0 : _d.route.route) === 'accountDetails') {
                const _afterAccountDetails = findAdjacentStep(flow, 1, flow.views[_nextStep].route);
                if (_afterAccountDetails !== -1) {
                    _nextStep = _afterAccountDetails;
                }
            }
            // the view being left behind, taken by identity so it survives being dropped from the flow
            let _currentRoute = flow.currentRoute;
            if (_nextStep === -1) {
                // no remaining valid steps, should not happen, go to first step as fallback
                _nextStep = 0;
                _currentRoute = (_e = flow.views[0]) === null || _e === void 0 ? void 0 : _e.route;
            }
            const _nextRoute = (_f = flow.views[_nextStep]) === null || _f === void 0 ? void 0 : _f.route;
            setFlow((prev) => (Object.assign(Object.assign({}, prev), { insideFlow: flow.insideFlow, currentStep: _nextStep, steps: flow.steps, currentRoute: _nextRoute, previousRoute: _currentRoute, nextRoute: _nextStep < (flow.views.length - 1) ? flow.views[_nextStep + 1].route : _nextRoute, fromRoute: _currentRoute, views: flow.views, currentView: flow.currentView, onNext: flow.onNext, setBackCallback: flow.setBackCallback, backCallback: flow.backCallback, direction: 'next' })));
        }
    };
    const handlePrevious = () => {
        var _a, _b;
        if (flow) {
            if (flow.backCallback) {
                flow.direction = 'prev';
                flow.backCallback();
                setFlow((prev) => (Object.assign(Object.assign({}, prev), { insideFlow: flow.insideFlow, currentStep: flow.currentStep, steps: flow.steps, currentRoute: flow.currentRoute, previousRoute: flow.currentRoute, nextRoute: flow.nextRoute, fromRoute: prev === null || prev === void 0 ? void 0 : prev.currentRoute, views: flow.views, currentView: flow.currentView, onNext: flow.onNext, setBackCallback: flow.setBackCallback, backCallback: undefined, direction: 'prev' })));
                return;
            }
            // Recompute which views are still valid before walking backwards. handleNext gets
            // this for free via handleInsideFlow; handlePrevious did not, so it chose the
            // previous step from a view list left over from the last forward transition. A view
            // can stop being valid while you are standing still — aborting the order on the
            // summary clears cart.order_id, which drops the payment step — and the decision
            // about where "back" goes was being made against the list from before that.
            //
            // BasicNav's mount-time checkFlow would recompute a moment later, so the step count
            // caught up on its own; what it could not undo was a navigation already decided.
            // determineSteps rewrites flow.views and flow.steps in place, so flow is current
            // from here on.
            determineSteps(flow);
            let _previousRoute = undefined;
            let _currentRoute = undefined;
            // look up the current step in the new flow with updated valid steps
            let _currentStep = flow.views.findIndex((view) => {
                var _a;
                return view.route.route === ((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route);
            });
            flow.direction = 'prev';
            if (_currentStep >= 0) {
                let _thisView = flow.views[_currentStep];
                if (_thisView && _thisView.subroutes) {
                    let _currentRoute = location.hash.substring(12);
                    if (_thisView.subroutes.includes(_currentRoute)) {
                        // we are actually on a subroute, and we got here using navigate, not onNext
                        if (_thisView.route.parentRoute) {
                            let _parentRoute = _thisView.route.parentRoute;
                            flow.views[_currentStep].route.route = _parentRoute;
                            flow.views[_currentStep].route.parentRoute = undefined;
                            flow.direction = "prev";
                            navigate(`/oa_widget_${_parentRoute}`);
                        }
                        else {
                            flow.direction = "prev";
                            navigate(`/oa_widget_${_thisView.route.route}`);
                        }
                        return;
                    }
                }
            }
            // Same identity-based scan as handleNext, in the other direction. Walking backwards
            // from the current INDEX resolved to the wrong view once the valid list changed, and
            // to flow.views[-1] (a TypeError on .requirements) when the current view had itself
            // been dropped from the flow.
            let _previousStep = findAdjacentStep(flow, -1);
            _currentRoute = flow.currentRoute;
            if (_previousStep === -1) {
                // already at the first valid step, nothing to go back to
                _previousStep = 0;
                _currentRoute = (_a = flow.views[0]) === null || _a === void 0 ? void 0 : _a.route;
            }
            _previousRoute = (_b = flow.views[_previousStep]) === null || _b === void 0 ? void 0 : _b.route;
            setFlow((prev) => (Object.assign(Object.assign({}, prev), { insideFlow: flow.insideFlow, currentStep: _previousStep, steps: flow.steps, currentRoute: _previousRoute, previousRoute: _previousStep > 0 ? flow.views[_previousStep - 1].route : _previousRoute, fromRoute: _currentRoute, nextRoute: _currentRoute, views: flow.views, currentView: flow.currentView, onNext: flow.onNext, setBackCallback: flow.setBackCallback, backCallback: flow.backCallback, direction: 'prev' })));
        }
    };
    const setBackCallback = (callback) => {
        if (flow) {
            setFlow((prev) => (Object.assign(Object.assign({}, prev), { insideFlow: flow.insideFlow, currentStep: flow.currentStep, steps: flow.steps, currentRoute: flow.currentRoute, fromRoute: flow.currentRoute, previousRoute: flow.previousRoute, nextRoute: flow.nextRoute, views: flow.views, currentView: flow.currentView, onNext: flow.onNext, setBackCallback: flow.setBackCallback, backCallback: callback, direction: "prev" })));
        }
    };
    const restart = () => {
        if (flow) {
            // Re-derive the valid views before picking one: flow.views is whatever the last
            // transition left behind, and on the very first navigation that list was built
            // before the apptypes — and everything else the requirements read — had loaded.
            determineSteps(flow);
            if (flow.views.length > 0) {
                navigate(`/oa_widget_${flow.views[0].route.route}`);
            }
        }
    };
    const setFlowReturn = (route) => {
        // a consumer route is not a step to come back to, so hopping from one menu screen to the
        // next keeps pointing at the step the consumer actually left
        if (route && !isConsumerRoute(route)) {
            flowReturn.current = route;
        }
    };
    const returnToFlow = () => {
        if (flow) {
            // the step they left is not necessarily still valid - signing out drops the account
            // steps, an aborted order drops the payment - so re-derive the flow before aiming at it
            determineSteps(flow);
            const _return = flowReturn.current;
            const _isValid = !!_return && flow.views.some((view) => {
                var _a;
                return view.route.route === _return
                    || view.route.parentRoute === _return
                    || !!((_a = view.subroutes) === null || _a === void 0 ? void 0 : _a.includes(_return));
            });
            flow.direction = 'prev';
            if (_isValid) {
                navigate(`/oa_widget_${_return}`);
            }
            else {
                restart();
            }
        }
    };
    useEffect(() => {
        if (flow && !(flow === null || flow === void 0 ? void 0 : flow.insideFlow)) {
            // restart()
        }
    }, [flow === null || flow === void 0 ? void 0 : flow.insideFlow]);
    // whatever the view we are leaving was waiting for, it is not what the visitor is looking at
    // any more - and from here on the transition puts up its own overlay
    useEffect(() => {
        setBusy(false);
    }, [flow === null || flow === void 0 ? void 0 : flow.currentRoute]);
    const value = {
        busy: busy,
        setBusy: setBusy,
        insideFlow: (_a = flow === null || flow === void 0 ? void 0 : flow.insideFlow) !== null && _a !== void 0 ? _a : false,
        currentStep: (_b = flow === null || flow === void 0 ? void 0 : flow.currentStep) !== null && _b !== void 0 ? _b : 0,
        previousRoute: flow === null || flow === void 0 ? void 0 : flow.previousRoute,
        currentRoute: flow === null || flow === void 0 ? void 0 : flow.currentRoute,
        fromRoute: flow === null || flow === void 0 ? void 0 : flow.currentRoute,
        nextRoute: flow === null || flow === void 0 ? void 0 : flow.nextRoute,
        steps: (_c = flow === null || flow === void 0 ? void 0 : flow.steps) !== null && _c !== void 0 ? _c : 0,
        views: (_d = flow === null || flow === void 0 ? void 0 : flow.views) !== null && _d !== void 0 ? _d : [],
        currentView: flow === null || flow === void 0 ? void 0 : flow.currentView,
        onNext: handleNext,
        onPrevious: handlePrevious,
        checkFlow: handleInsideFlow,
        restart: restart,
        title: title,
        setTitle: setTitle,
        setFlowReturn: setFlowReturn,
        returnToFlow: returnToFlow,
        setBackCallback: setBackCallback,
        direction: "next"
    };
    return (_jsx(FlowContext.Provider, { value: value, children: children }));
};
export default FlowProvider;
